import React, { useEffect, useState } from "react";
import { X, Loader2, EyeOff, Eye } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Fase 14.5 · Modal para ocultar / mostrar thread o post con motivo predefinido.
 *
 * Props:
 *   - targetType: "thread" | "post"
 *   - targetId: string
 *   - currentlyHidden: bool
 *   - onDone: () => void   // llamar tras éxito
 *   - onClose: () => void
 */
export default function HideThreadModal({ targetType, targetId, currentlyHidden,
                                            onDone, onClose }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("spam");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/forums/moderation/reasons").then((r) => {
      setReasons(r.data.items || []);
    }).catch(() => {});
  }, []);

  const hiding = !currentlyHidden;

  const submit = async () => {
    setBusy(true);
    try {
      const url = targetType === "thread"
        ? `/forums/threads/${targetId}/hide`
        : `/forums/posts/${targetId}/hide`;
      const payload = { hidden: hiding };
      if (hiding) {
        payload.reason = reason;
        if (note.trim()) payload.note = note.trim();
      }
      await api.patch(url, payload);
      toast.success(hiding ? "Contenido oculto" : "Contenido restaurado");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="hide-target-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-3"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-orange-500 font-semibold flex items-center gap-1">
              {hiding ? <><EyeOff className="w-3 h-3" /> Ocultar</> : <><Eye className="w-3 h-3" /> Restaurar</>}
            </p>
            <h3 className="text-xl font-bold text-white mt-1 font-display">
              {hiding
                ? `¿Ocultar ${targetType === "thread" ? "este tema" : "esta respuesta"}?`
                : `¿Restaurar visibilidad?`}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {hiding && (
          <>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                Motivo predefinido
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="hide-reason-select"
                className="w-full mt-1 bg-[#0F172A] border border-gray-800 rounded-md px-3 py-2 text-sm text-white"
              >
                {reasons.map((r) => (
                  <option key={r.code} value={r.code}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">
                Nota interna (opcional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                rows={3}
                data-testid="hide-note-input"
                placeholder="Detalles para el registro de moderación…"
                className="w-full mt-1 bg-[#0F172A] border border-gray-800 rounded-md px-3 py-2 text-sm text-white"
              />
              <p className="text-right text-[10px] text-gray-500 font-mono mt-0.5">{note.length}/500</p>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-gray-700 text-gray-300 hover:bg-white/5 text-sm"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="hide-confirm-btn"
            className={`inline-flex items-center gap-1 px-6 py-2 rounded-md text-sm font-semibold text-white ${
              hiding ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"
            } disabled:opacity-60`}
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {hiding ? "Ocultar" : "Restaurar"}
          </button>
        </div>
      </div>
    </div>
  );
}
